import { supabase } from '../lib/supabase'
import { format, addDays } from 'date-fns'

export interface WeeklySummaryTgReport {
  subcontractor_id: string
  subcontractor_name: string
  subcontractor_color: string
  tg_status: string | null
  report_text: string | null
  reported_at: string | null
  reported_articles: ReportedArticleEntry[]
  total_hours: number
}

export interface ReportedArticleEntry {
  article_name: string
  actual_quantity: number | null
  unit: string | null
  notes: string
  is_expense: boolean
}

export interface WeeklySummaryAssignment {
  id: string
  assignment_number: number | null
  title: string
  start_date: string
  start_time: string | null
  end_time: string | null
  status: string
  customer_name: string
  customer_id: string
  subcontractor_reports: WeeklySummaryTgReport[]
}

export interface WeeklySummaryData {
  assignments: WeeklySummaryAssignment[]
  admin_notes: string | null
  admin_notes_id: string | null
  total_hours: number
  total_assignments: number
  reported_count: number
  unreported_count: number
}

// Fetch the full weekly summary for a given week (weekStart = Monday date as yyyy-MM-dd)
export async function fetchWeeklySummary(weekStart: string): Promise<WeeklySummaryData> {
  const weekEnd = format(addDays(new Date(weekStart), 6), 'yyyy-MM-dd')

  // Fetch assignments, TG reports, and weekly notes in parallel
  const [assignmentsResult, weekNoteResult] = await Promise.all([
    supabase
      .from('assignments')
      .select(`
        id,
        assignment_number,
        title,
        start_date,
        start_time,
        end_time,
        status,
        customers!inner(id, full_name),
        assignment_subcontractors(
          subcontractor_id,
          tg_status,
          report_text,
          reported_at,
          reported_articles,
          subcontractors(id, name, color)
        )
      `)
      .gte('start_date', weekStart)
      .lte('start_date', weekEnd)
      .order('start_date', { ascending: true })
      .order('start_time', { ascending: true }),
    supabase
      .from('weekly_summaries')
      .select('id, admin_notes')
      .eq('week_start', weekStart)
      .maybeSingle()
  ])

  if (assignmentsResult.error) {
    console.error('Error fetching weekly assignments:', assignmentsResult.error)
    throw assignmentsResult.error
  }

  // Get article IDs from all reported_articles to look up which ones are hour-based
  const articleIds = new Set<string>()
  for (const assignment of assignmentsResult.data || []) {
    for (const tgReport of (assignment.assignment_subcontractors as any[]) || []) {
      const reportedArticles = (tgReport.reported_articles as any[]) || []
      for (const ra of reportedArticles) {
        if (ra.article_id && !ra.is_expense) {
          articleIds.add(ra.article_id)
        }
      }
    }
  }

  // Fetch article units to identify hour-articles
  let articleUnits: Record<string, string> = {}
  if (articleIds.size > 0) {
    const { data: articles } = await supabase
      .from('articles')
      .select('id, unit')
      .in('id', Array.from(articleIds))

    for (const a of articles || []) {
      articleUnits[a.id] = a.unit || 'st'
    }
  }

  // Build the summary
  const assignments: WeeklySummaryAssignment[] = []
  let totalHours = 0
  let reportedCount = 0
  let unreportedCount = 0

  for (const row of assignmentsResult.data || []) {
    const customer = row.customers as any
    const tgReports: WeeklySummaryTgReport[] = []

    for (const tgReport of (row.assignment_subcontractors as any[]) || []) {
      const sub = tgReport.subcontractors as any
      const reportedArticles = (tgReport.reported_articles as any[]) || []

      let tgHours = 0
      const articles: ReportedArticleEntry[] = []

      for (const ra of reportedArticles) {
        const isExpense = ra.is_expense || false
        const unit = ra.article_id ? (articleUnits[ra.article_id] || ra.unit || 'st') : (ra.unit || 'st')
        const quantity = ra.actual_quantity || 0

        // Count hours (unit = 'tim' or 'timme' or 'h')
        if (!isExpense && unit && ['tim', 'timme', 'h'].includes(unit.toLowerCase())) {
          tgHours += quantity
        }

        articles.push({
          article_name: ra.article_name || 'Okänd artikel',
          actual_quantity: ra.actual_quantity,
          unit: unit,
          notes: ra.notes || '',
          is_expense: isExpense,
        })
      }

      totalHours += tgHours

      if (tgReport.reported_at) {
        reportedCount++
      } else {
        unreportedCount++
      }

      tgReports.push({
        subcontractor_id: sub?.id || tgReport.subcontractor_id,
        subcontractor_name: sub?.name || 'Okänd',
        subcontractor_color: sub?.color || '#888888',
        tg_status: tgReport.tg_status,
        report_text: tgReport.report_text,
        reported_at: tgReport.reported_at,
        reported_articles: articles,
        total_hours: tgHours,
      })
    }

    assignments.push({
      id: row.id,
      assignment_number: row.assignment_number,
      title: row.title,
      start_date: row.start_date,
      start_time: row.start_time,
      end_time: row.end_time,
      status: row.status,
      customer_name: customer?.full_name || 'Okänd kund',
      customer_id: customer?.id || '',
      subcontractor_reports: tgReports,
    })
  }

  return {
    assignments,
    admin_notes: weekNoteResult.data?.admin_notes || null,
    admin_notes_id: weekNoteResult.data?.id || null,
    total_hours: totalHours,
    total_assignments: assignments.length,
    reported_count: reportedCount,
    unreported_count: unreportedCount,
  }
}

// Save admin notes for a week (upsert)
export async function saveWeeklyNotes(
  weekStart: string,
  notes: string,
  existingId: string | null,
  userId: string
): Promise<{ id: string }> {
  if (!notes.trim()) {
    // Delete if cleared
    if (existingId) {
      await supabase.from('weekly_summaries').delete().eq('id', existingId)
    }
    return { id: '' }
  }

  const { data, error } = await supabase
    .from('weekly_summaries')
    .upsert(
      {
        ...(existingId ? { id: existingId } : {}),
        week_start: weekStart,
        admin_notes: notes,
        ...(existingId ? { updated_by: userId } : { created_by: userId, updated_by: userId }),
      },
      { onConflict: 'week_start' }
    )
    .select('id')
    .single()

  if (error) {
    console.error('Error saving weekly notes:', error)
    throw error
  }

  return { id: data.id }
}
